import React, { useContext } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import Colors from '@components/shared/Colors';
import { P } from '@components/shared/Typography';
import { SOLID_TICK_SVG } from '@constants';
import { getPriceText } from '@utils/price';

interface Props {
  location?: string;
}

const FreeShippingRequirement: React.FC<Props> = ({ location }) => {
  const { t } = useTranslation();
  const { state } = useContext(Context);
  const { price, currency, freeShippingRequirement, shippingCost } = state;
  const moreAmount = freeShippingRequirement - price;
  const percentage = (price / freeShippingRequirement) * 100;
  const requirementText = getPriceText(freeShippingRequirement, state.country, currency, t);

  if (shippingCost.originalShippingPrice <= 0 || (shippingCost.shippingPrice === 0 && moreAmount > 0)) return null;

  if (moreAmount > 0) {
    const moreAmountText = getPriceText(moreAmount, state.country, currency, t);

    return (
      <Container>
        <Text className={location}>
          <StyledP>
            <strong>{t('components.shipping-free-requirement-2', { price: requirementText.fullString })}</strong>
            {t('components.shipping-free-requirement-more-2', { price: moreAmountText.fullString })}
          </StyledP>
        </Text>
        <ProgressBarContainer>
          <ProgressBar style={{ width: `${percentage}%` }} />
        </ProgressBarContainer>
      </Container>
    );
  }

  return (
    <Container className="requirement-met">
      <Text className={location}>
        <img src={SOLID_TICK_SVG} alt="tick" style={{ marginRight: 10 }} />
        <StyledP>
          {t('components.shipping-free-requirement-2', {
            price: requirementText.fullString
          })}
        </StyledP>
      </Text>
    </Container>
  );
};

export default FreeShippingRequirement;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 15px 0 0;

  &.requirement-met {
    border-bottom: 1px solid ${Colors.ecru};
    padding: 15px 0;
  }
`;

const Text = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  text-align: center;
  justify-content: center;
  width: 100%;
  padding: 0 15px;

  &.basket {
    @media (min-width: 1024px) {
      justify-content: flex-start;
      padding: 0 15px 0 0;
      text-align: left;
    }
  }
`;

const StyledP = styled(P)`
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 1.3125px;
  text-transform: uppercase;
  margin: 0;

  strong {
    margin-right: 4px;
  }
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 5px;
  position: relative;
  background: ${Colors.ecru};
  margin-top: 10px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background: ${Colors.gold};
  transition: width 0.2s ease-in-out;
`;
